import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, CreditCard, Sparkles, Boxes, MessageSquare, ArrowUpRight, LogOut, Activity } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { id: "overview", label: "Overview", Icon: LayoutDashboard },
  { id: "subscription", label: "Subscription", Icon: Sparkles },
  { id: "billing", label: "Billing", Icon: CreditCard },
  { id: "products", label: "My Products", Icon: Boxes },
  { id: "ai", label: "AI Workspace", Icon: MessageSquare },
];

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("overview");
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/me/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-ink-400" data-testid="dashboard-page">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside className="lg:border-r border-white/8 lg:min-h-screen p-6 flex flex-col">
          <Link to="/"><Logo /></Link>
          <div className="mt-10 space-y-1">
            {NAV.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActive(id)}
                data-testid={`dash-nav-${id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  active === id ? "bg-gold-500/10 text-gold-200 border border-gold-500/25" : "text-white/65 hover:text-white hover:bg-white/5 border border-transparent"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
          <div className="mt-auto pt-8">
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="dash-admin-link" className="mb-3 w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border border-gold-500/30 bg-gold-500/[0.06] text-gold-200 hover:bg-gold-500/[0.12]">
                <Sparkles className="h-4 w-4" /> Admin Console
              </Link>
            )}
            <div className="glass-card p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Signed in</div>
              <div className="mt-1 text-sm text-white truncate" data-testid="dash-user-email">{user?.email}</div>
              <div className="mt-1 text-xs text-gold-300 capitalize">{user?.plan} plan</div>
              <button onClick={logout} className="mt-3 w-full pill pill-ghost text-xs justify-center" data-testid="dash-logout">
                <LogOut className="h-3.5 w-3.5" /> Logout
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="p-6 md:p-10">
          <div className="max-w-5xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow text-gold-300">Client Dashboard</div>
                <h1 className="mt-2 font-serif text-3xl md:text-4xl text-white">
                  Welcome, <span className="gold-text italic">{user?.name?.split(" ")[0] || "Client"}</span>.
                </h1>
              </div>
              <Link to="/billing" className="hidden md:inline-flex pill pill-gold text-sm" data-testid="dash-upgrade-btn">
                Upgrade Plan <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            {active === "overview" && <Overview sub={sub} />}
            {active === "subscription" && <Subscription sub={sub} user={user} />}
            {active === "billing" && <BillingPanel />}
            {active === "products" && <ProductsPanel />}
            {active === "ai" && <AIPanel />}
          </div>
        </main>
      </div>
    </div>
  );
}

function Overview({ sub }) {
  const stats = [
    { k: "Active Plan", v: sub?.plan || "Starter", Icon: Sparkles },
    { k: "API Calls", v: "1,284", Icon: Activity },
    { k: "Products", v: "2", Icon: Boxes },
    { k: "Open Tickets", v: "0", Icon: MessageSquare },
  ];
  return (
    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="overview-panel">
      {stats.map((s, i) => (
        <div key={i} className="glass-card p-5">
          <s.Icon className="h-4 w-4 text-gold-300" />
          <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">{s.k}</div>
          <div className="mt-1 font-serif text-2xl text-white capitalize">{s.v}</div>
        </div>
      ))}
      <div className="col-span-2 lg:col-span-4 glass-card p-6 mt-2">
        <div className="font-serif text-xl text-white">Quick actions</div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/billing" className="pill pill-ghost text-sm">Manage billing</Link>
          <Link to="/#marketplace" className="pill pill-ghost text-sm">Open AI Marketplace</Link>
          <Link to="/#contact" className="pill pill-ghost text-sm">Contact support</Link>
        </div>
      </div>
    </div>
  );
}

function Subscription({ sub, user }) {
  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="subscription-panel">
      <div className="glass-card p-6">
        <div className="eyebrow text-gold-300">Current Plan</div>
        <div className="mt-3 font-serif text-3xl text-white capitalize">{sub?.plan || user?.plan || "Starter"}</div>
        <div className="mt-1 text-sm text-white/55">Manage your AI Marketplace and product modules.</div>
        <Link to="/billing" className="mt-5 pill pill-gold text-sm inline-flex" data-testid="sub-change-plan">Change plan</Link>
      </div>
      <div className="glass-card p-6">
        <div className="eyebrow text-gold-300">Recent Activity</div>
        <div className="mt-4 space-y-3">
          {(sub?.history || []).slice(0, 4).map((h) => (
            <div key={h.id} className="flex items-center justify-between text-sm">
              <div>
                <div className="text-white capitalize">{h.plan} · {h.cycle}</div>
                <div className="text-xs text-white/45">{new Date(h.created_at).toLocaleString()}</div>
              </div>
              <div className="text-gold-300 font-mono text-xs">${h.amount_usd}</div>
            </div>
          ))}
          {(!sub || (sub.history || []).length === 0) && <div className="text-sm text-white/45">No subscription history yet.</div>}
        </div>
      </div>
    </div>
  );
}

function BillingPanel() {
  return (
    <div className="mt-8" data-testid="billing-panel">
      <div className="glass-card p-8">
        <div className="eyebrow text-gold-300">Billing</div>
        <div className="mt-3 font-serif text-2xl text-white">Manage payments & invoices</div>
        <p className="mt-2 text-sm text-white/55">Choose a plan and a payment method. Checkout integrations are coming online soon.</p>
        <Link to="/billing" className="mt-5 inline-flex pill pill-gold text-sm">Go to Billing</Link>
      </div>
    </div>
  );
}

function ProductsPanel() {
  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-panel">
      {["AI Queue Pro", "AI Marketplace", "AI Voice Bot"].map((p) => (
        <div key={p} className="glass-card p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gold-300">Active</div>
          <div className="mt-2 font-serif text-xl text-white">{p}</div>
          <div className="mt-1 text-sm text-white/55">Module deployed and ready.</div>
        </div>
      ))}
    </div>
  );
}

function AIPanel() {
  return (
    <div className="mt-8 glass-card p-8" data-testid="ai-panel">
      <div className="eyebrow text-gold-300">AI Workspace</div>
      <div className="mt-3 font-serif text-2xl text-white">11 models. One workspace.</div>
      <p className="mt-2 text-sm text-white/55">Visit the public marketplace to chat with Claude live, or upgrade for full model access.</p>
      <Link to="/#marketplace" className="mt-5 inline-flex pill pill-gold text-sm">Open Marketplace</Link>
    </div>
  );
}
